import axios, {
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosResponse,
  RawAxiosRequestHeaders,
} from "axios";
import { BaseQueryFn } from "@reduxjs/toolkit/query/react";
import {
  getAccessToken,
  getCorrelationID,
  getIdToken,
  getNextEndpoint,
  getRefreshToken,
  setCorrelationID,
  setNextEndpoint,
} from "src/utils/SessionManagement";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { handleError, refreshAuthLogic } from "src/utils/tokenRefreshLogic";
import { Buffer } from "buffer";
import { getDeviceId, getSessionId } from "@amplitude/analytics-browser";

// Function to decrypt data
function decrypt(encryptedText: string): string {
  return Buffer.from(encryptedText, "base64").toString("binary");
}

axios.interceptors.request.use(async (request: InternalAxiosRequestConfig) => {
  if (request.headers) {
    request.headers["accessToken"] = getAccessToken();
    request.headers["idToken"] = getIdToken();
    request.headers["refreshToken"] = getRefreshToken();
    request.headers["sessionId"] = getSessionId();
    request.headers["deviceId"] = getDeviceId();

    if (request.url === getNextEndpoint()) {
      request.headers["correlation-id"] = getCorrelationID();
    }
  }
  return request;
});

// axios response interceptors
axios.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    if (response.headers["correlation-id"]) {
      const decrypted = decrypt(response.headers["correlation-id"]);
      const parts = decrypted.split(",");
      if (parts.length >= 2) {
        // The first part is the correlation ID (trim to remove any leading/trailing spaces)
        const correlationID = parts[0].trim();

        // The second part is the next endpoint (trim to remove any leading/trailing spaces)
        const nextEndpoint = parts[1].trim();

        if (correlationID && nextEndpoint) {
          setCorrelationID(correlationID);
          setNextEndpoint(nextEndpoint);
        }
      } else {
        // Handle the case where the decrypted data does not have the expected format
        console.warn("Invalid decrypted data format");
      }
    }
    return response;
  },
  (error: AxiosError): Promise<never> => {
    // error state
    return Promise.reject(error);
  },
);

const refreshAuthLogicOptions = {
  statusCodes: [440],
  pauseInstanceWhileRefreshing: true,
};

// Auth interceptor
createAuthRefreshInterceptor(axios, refreshAuthLogic, refreshAuthLogicOptions);

// Axios base query
const axiosBaseQuery = (
  { baseUrl }: { baseUrl: string } = { baseUrl: "" },
): BaseQueryFn<
  {
    body?: InternalAxiosRequestConfig["data"];
    url: string;
    method?: InternalAxiosRequestConfig["method"];
    params?: InternalAxiosRequestConfig["params"];
    // Per-request headers from the mutation/query's `query` function. The
    // request interceptor already injects auth headers (accessToken,
    // idToken, etc.), so use this for endpoint-specific headers like
    // X-Withdrawal-Prepared-Token. We use RawAxiosRequestHeaders (the
    // plain-object form) rather than AxiosRequestHeaders (the class form)
    // because mutation `query` functions return plain object literals.
    headers?: RawAxiosRequestHeaders;
    // Per-request timeout (ms). axios defaults to none, so a request that a
    // mobile network silently drops stays "pending" forever — and RTK Query
    // dedupes every later refetch of that cache key onto the hung request.
    timeout?: number;
  },
  unknown,
  { status: number | undefined; message: string }
> => {
  return async (params) => {
    try {
      const result = await axios({
        data: params.body,
        baseURL: baseUrl,
        method: params.method || "GET",
        url: params.url,
        params: params.params,
        // Without forwarding this, custom headers built by RTK Query
        // mutations (like the withdrawal-prepared token) are silently
        // dropped — they never reach the server. The auth interceptor
        // re-applies its own headers on top, so we don't lose those.
        headers: params.headers,
        timeout: params.timeout,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError<any>;
      handleError(err);
      return {
        error: {
          status: err.response?.status,
          message: err.response?.data.message,
        },
      };
    }
  };
};

export const uninterceptedAxiosInstance = axios.create();

export default axiosBaseQuery;
